import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Plus, 
  CalendarIcon, 
  Pencil, 
  Trash2, 
  Package,
  Loader2,
  ClipboardList,
  AlertCircle
} from 'lucide-react';

const OrdersPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar' || language === 'ku';
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    items: [],
    notes: '',
    amount_to_collect: 0
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [selectedDate]);

  const fetchInitialData = async () => {
    try {
      const [customersRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/api/customers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/products`, { headers: getAuthHeader() })
      ]);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API_URL}/api/orders?date=${dateStr}`, {
        headers: getAuthHeader()
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('حدث خطأ في تحميل الطلبات');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setSelectedOrder(null);
    setFormData({
      customer_id: '',
      date: format(selectedDate, 'yyyy-MM-dd'),
      items: products.map(p => ({ product_id: p.id, quantity: 0 })),
      notes: '',
      amount_to_collect: 0
    });
    setDialogOpen(true);
  };

  const openEditDialog = (order) => {
    setSelectedOrder(order);
    // Merge existing items with all products
    const items = products.map(p => {
      const existing = order.items.find(i => i.product_id === p.id);
      return { product_id: p.id, quantity: existing ? existing.quantity : 0 };
    });
    setFormData({
      customer_id: order.customer_id,
      date: order.date,
      items: items,
      notes: order.notes,
      amount_to_collect: order.amount_to_collect || 0
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (order) => {
    setSelectedOrder(order);
    setDeleteDialogOpen(true);
  };

  const updateItemQuantity = (productId, quantity) => {
    setFormData({
      ...formData,
      items: formData.items.map(item =>
        item.product_id === productId ? { ...item, quantity: parseInt(quantity) || 0 } : item
      )
    });
  };

  // Get selected customer balance info
  const getSelectedCustomerInfo = () => {
    if (!formData.customer_id) return null;
    const customer = customers.find(c => c.id === formData.customer_id);
    return customer;
  };

  const selectedCustomerInfo = getSelectedCustomerInfo();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error(t('selectCustomer') || 'الرجاء اختيار الزبون');
      return;
    }

    const hasItems = formData.items.some(item => item.quantity > 0);
    if (!hasItems) {
      toast.error(t('enterQuantity') || 'الرجاء إدخال كمية واحدة على الأقل');
      return;
    }

    setFormLoading(true);

    try {
      // Filter out zero quantity items
      const itemsToSend = formData.items.filter(item => item.quantity > 0);
      
      if (selectedOrder) {
        await axios.put(
          `${API_URL}/api/orders/${selectedOrder.id}`,
          { items: itemsToSend, notes: formData.notes, amount_to_collect: formData.amount_to_collect },
          { headers: getAuthHeader() }
        );
        toast.success(t('orderUpdated') || 'تم تحديث الطلب بنجاح');
      } else {
        await axios.post(
          `${API_URL}/api/orders`,
          { ...formData, items: itemsToSend },
          { headers: getAuthHeader() }
        );
        toast.success(t('orderAdded') || 'تم إضافة الطلب بنجاح');
      }
      setDialogOpen(false);
      fetchOrders();
    } catch (error) {
      console.error('Error saving order:', error);
      if (error.response?.status === 400) {
        toast.error(t('orderExistsError') || 'يوجد طلب مسبق لهذا الزبون في هذا التاريخ');
      } else {
        toast.error(t('saveError') || 'حدث خطأ في حفظ الطلب');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/orders/${selectedOrder.id}`,
        { headers: getAuthHeader() }
      );
      toast.success(t('orderDeleted') || 'تم حذف الطلب بنجاح');
      setDeleteDialogOpen(false);
      fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error(t('deleteError') || 'حدث خطأ في حذف الطلب');
    }
  };

  const formatCurrency = (amount) => {
    return amount.toFixed(2);
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown';
  };

  // Get customers who don't have an order for selected date
  const availableCustomers = selectedOrder 
    ? customers 
    : customers.filter(c => !orders.find(o => o.customer_id === c.id));

  return (
    <div data-testid="orders-page" className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-stone-900">{t('orders')}</h1>
          <p className="text-stone-500 mt-1">{orders.length} {t('orderCount')}</p>
        </div>
        <div className="flex gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start text-right font-normal">
                <CalendarIcon className="ms-2 h-4 w-4" />
                {format(selectedDate, 'yyyy/MM/dd')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button
            data-testid="add-order-btn"
            onClick={openAddDialog}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
            disabled={availableCustomers.length === 0}
          >
            <Plus className="w-5 h-5 me-2" />
            {t('addOrder')}
          </Button>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-10 h-10"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="empty-state">
          <ClipboardList className="empty-state-icon" />
          <h3 className="empty-state-title">{t('noOrders')}</h3>
          <p className="empty-state-description">{t('noOrdersForDate')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orders.map((order) => {
            const boxesPending = order.boxes_to_return - order.boxes_returned;
            return (
              <Card key={order.id} className={`border-stone-200 hover:shadow-md transition-shadow ${order.created_by === 'customer' ? 'border-blue-300 bg-blue-50/30' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{order.customer_name}</CardTitle>
                        {order.created_by === 'customer' && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">من الزبون</span>
                        )}
                        {order.edited && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">معدّل</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(order)}
                        className="text-stone-500 hover:text-amber-600"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteDialog(order)}
                        className="text-stone-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Products */}
                    <div className="flex flex-wrap gap-2">
                      {order.items.map((item, idx) => (
                        <span key={idx} className="bg-stone-100 px-3 py-1.5 rounded-lg text-sm">
                          {getProductName(item.product_id)}: <span className="font-bold">{item.quantity}</span>
                        </span>
                      ))}
                    </div>

                    {/* Boxes Status */}
                    {boxesPending > 0 && (
                      <div className="flex items-center gap-2 text-red-600 bg-red-50 p-2 rounded-lg">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">{boxesPending} صندوق لم يُرجع</span>
                      </div>
                    )}

                    {order.notes && (
                      <p className="text-sm text-stone-500 bg-stone-50 rounded p-2">
                        {order.notes}
                      </p>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                      <div>
                        <span className="text-sm text-stone-500">المبلغ:</span>
                        <span className="ms-2 font-bold">{formatCurrency(order.total_price)} €</span>
                      </div>
                      {order.tax_amount > 0 && (
                        <div>
                          <span className="text-sm text-stone-500">+ ضريبة:</span>
                          <span className="ms-2 font-bold text-emerald-600">{formatCurrency(order.total_with_tax)} €</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-6 pb-0 flex-shrink-0">
            <DialogTitle>{selectedOrder ? t('editOrder') : t('addOrder')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 pt-4 space-y-4">
            <div className="space-y-2">
              <Label>{t('customerName')} *</Label>
              <Select
                value={formData.customer_id}
                onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                disabled={!!selectedOrder}
              >
                <SelectTrigger data-testid="customer-select">
                  <SelectValue placeholder={t('selectCustomer')} />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100] max-h-[300px]">
                  {(selectedOrder ? customers : availableCustomers).map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      <span className="flex items-center gap-2">
                        <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs font-bold text-amber-700">
                          {customer.delivery_order}
                        </span>
                        {customer.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customer Balance Info */}
            {selectedCustomerInfo && (
              <div className={`p-4 rounded-lg border-2 ${
                selectedCustomerInfo.balance > 0 
                  ? 'bg-red-50 border-red-200' 
                  : selectedCustomerInfo.balance < 0 
                    ? 'bg-green-50 border-green-200'
                    : 'bg-stone-50 border-stone-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-stone-700">{t('customerBalance') || 'رصيد الزبون'}:</span>
                  <span className={`text-xl font-bold ${
                    selectedCustomerInfo.balance > 0 
                      ? 'text-red-600' 
                      : selectedCustomerInfo.balance < 0 
                        ? 'text-green-600'
                        : 'text-stone-600'
                  }`}>
                    {selectedCustomerInfo.balance > 0 ? '' : selectedCustomerInfo.balance < 0 ? '-' : ''}
                    €{Math.abs(selectedCustomerInfo.balance).toFixed(2)}
                  </span>
                </div>
                <div className="text-sm text-stone-600 space-y-1">
                  <div className="flex justify-between">
                    <span>{t('totalDebt')}:</span>
                    <span className="font-medium">€{selectedCustomerInfo.total_debt?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('totalPaid')}:</span>
                    <span className="font-medium text-green-600">€{selectedCustomerInfo.total_paid?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
                {selectedCustomerInfo.balance > 0 && (
                  <p className="text-xs text-red-600 mt-2 font-medium">
                    ⚠ {t('customerHasBalance') || 'الزبون عليه مبلغ متبقي'} €{selectedCustomerInfo.balance.toFixed(2)}
                  </p>
                )}
                {selectedCustomerInfo.balance === 0 && selectedCustomerInfo.total_debt > 0 && (
                  <p className="text-xs text-green-600 mt-2 font-medium">
                    ✓ {t('accountSettled') || 'الحساب مسدد بالكامل'}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('date')}</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                disabled={!!selectedOrder}
              />
            </div>

            <div className="border-t border-stone-200 pt-4">
              <h4 className="font-bold text-stone-900 mb-3">{t('quantity') || 'الكميات'}</h4>
              <div className="grid grid-cols-2 gap-3">
                {products.map((product) => {
                  const item = formData.items.find(i => i.product_id === product.id);
                  return (
                    <div key={product.id} className="space-y-1">
                      <Label className="text-sm font-medium">{product.name}</Label>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-10 w-10 p-0 text-lg font-bold"
                          onClick={() => updateItemQuantity(product.id, Math.max(0, (item?.quantity || 0) - 1))}
                        >
                          -
                        </Button>
                        <Input
                          type="number"
                          min="0"
                          inputMode="numeric"
                          value={item?.quantity || 0}
                          onChange={(e) => updateItemQuantity(product.id, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="h-10 text-center text-lg font-bold flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-10 w-10 p-0 text-lg font-bold"
                          onClick={() => updateItemQuantity(product.id, (item?.quantity || 0) + 1)}
                        >
                          +
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('notes')}</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('optionalNotes')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('amountToCollect') || 'مبلغ للاستلام من الزبون'} (€)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={formData.amount_to_collect}
                  onChange={(e) => setFormData({ ...formData, amount_to_collect: parseFloat(e.target.value) || 0 })}
                  onFocus={(e) => e.target.select()}
                  placeholder="0.00"
                  className="text-lg font-bold flex-1"
                />
                {selectedCustomerInfo && selectedCustomerInfo.balance > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => setFormData({ ...formData, amount_to_collect: selectedCustomerInfo.balance })}
                  >
                    {t('remainingBalance') || 'الرصيد المتبقي'} €{selectedCustomerInfo.balance.toFixed(2)}
                  </Button>
                )}
              </div>
              <p className="text-xs text-stone-500">{t('amountToCollectHint') || 'سيظهر للسائق في ورقة التوصيل'}</p>
            </div>
          </form>
          <DialogFooter className="gap-2 p-6 pt-4 border-t flex-shrink-0">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-amber-600 hover:bg-amber-700"
              disabled={formLoading}
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedOrder ? t('edit') : t('add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteOrderConfirm') || 'هل أنت متأكد من حذف هذا الطلب؟'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteOrderWarning') || 'سيتم حذف طلب'} "{selectedOrder?.customer_name}". {t('cannotUndo') || 'هذا الإجراء لا يمكن التراجع عنه.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrdersPage;
